// src/pages/TeacherDashboard.jsx
// 教員ダッシュボード
//
// 並べ方は2種類ある（「表示の設定」で人ごとに切り替え、既定は標準）【2026-09-18】
//   標準 (priority) … よく使う4つ → 出席受付への案内 → 要対応 → 数字 → 一覧
//                     出席受付は画面が大きいのでダッシュボードには埋め込まず、
//                     いまの状況だけ1行で見せて /otp へ送る【2026-09-18】。
//                     埋め込むと、受付中のときに開始フォーム（授業の選択）が
//                     隠れる仕様と噛み合わず、授業が選べないように見えてしまう。
//   元の表示 (classic) … 2026-09-17 までの並び。統計4枚 → クイックアクション → 表。
//                        こちらは中身を変えていない。
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AttendanceStartCard from '../components/AttendanceStartCard';
import Icon from '../components/Icon';
import useDashboardSettings from '../hooks/useDashboardSettings';
import {
  LinkStrip, AlertCard, StatTiles, StatTile, Sparkline, Fold,
  StatusBadge, EmptyState, TableWrap, formatTime,
} from '../components/DashboardParts';

export default function TeacherDashboard({ user }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  // いま受付中のもの。1行の案内と「閉じ忘れ」の判定に使う
  const [openSessions, setOpenSessions] = useState([]);
  const [otpCount, setOtpCount]         = useState(0);
  const [dash] = useDashboardSettings();

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/dashboard/teacher', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(d => {
        if (d.success) setData(d.dashboard);
        else setErr(d.message || 'ダッシュボード取得失敗');
      })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  // 受付中のセッションとOTP。取れなくてもダッシュボードは出す
  useEffect(() => {
    const auth = { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') };
    fetch('/api/attendance/sessions/history?status=active&limit=20', { headers: auth })
      .then(r => r.json())
      .then(d => setOpenSessions(((d && (d.sessions || d.data || d.rows)) || []).map(x => ({
        className:   x.class_name,
        sessionDate: x.session_date,
        present:     Number(x.present) || 0,
        late:        Number(x.late) || 0,
      }))))
      .catch(() => {});
    fetch('/api/attendance/otp/active', { headers: auth })
      .then(r => r.json())
      .then(d => setOtpCount(((d && d.success && d.codes) || []).length))
      .catch(() => {});
  }, []);

  if (loading) return <div className="page-container"><div className="card"><p>読み込み中...</p></div></div>;
  if (err)     return <div className="page-container"><div className="card"><p style={{color:'#c62828'}}>{err}</p></div></div>;

  const {
    overview = {}, classes = [], today_classes = [],
    recent_activity = [], low_attendance_alerts = [], monthly_trend = []
  } = data || {};

  const classic = dash.layout === 'classic';

  // 並べ方の切り替えはアカウント設定の「画面の表示」に置いてある【2026-09-18】
  const header = (
    <div className="page-header">
      <h1>教員ダッシュボード</h1>
      <p>ようこそ、{user.name || user.student_id} 先生</p>
    </div>
  );

  // よく使う4つ。元の色つきカードのまま【2026-09-18】
  const quickActions = (
    <div className="quick-actions">
      <QuickCard to="/otp"      icon={<Icon name="hash" size={22}/>}  title="出席受付"    desc="ビーコン・コードで受付を開始" color="#3b82f6"/>
      <QuickCard to="/classes"  icon={<Icon name="book" size={22}/>}  title="授業管理"    desc="授業・曜日時限・履修者" color="#8b5cf6"/>
      <QuickCard to="/live"     icon={<Icon name="radio" size={22}/>} title="リアルタイム" desc="出席状況をライブ表示" color="#0ea5e9"/>
    </div>
  );

  // ── 元の表示 ───────────────────────────────────────────────
  if (classic) {
    return (
      <div className="page-container">
        {header}

        <div className="stats-grid">
          <StatCard label="担当授業"   value={overview.total_classes        || 0} unit="科目" color="#3b82f6"/>
          <StatCard label="総セッション" value={overview.total_sessions      || 0} unit="回"   color="#22c55e"/>
          <StatCard label="出席記録"   value={overview.total_attendance_records || 0} unit="件" color="#f59e0b"/>
          <StatCard label="平均出席率"  value={(overview.average_attendance_rate || 0) + '%'} unit="全授業" color="#8b5cf6"/>
        </div>

        {quickActions}

        <AttendanceStartCard user={user} classes={classes}/>

        {today_classes.length > 0 && (
          <div className="card">
            <div className="card-header"><h2>本日の授業</h2></div>
            <TodayTable rows={today_classes}/>
          </div>
        )}

        <div className="card">
          <div className="card-header"><h2>担当授業</h2></div>
          {classes.length === 0
            ? <EmptyState text="担当授業がありません"/>
            : <ClassesTable rows={classes}/>}
        </div>

        {low_attendance_alerts.length > 0 && (
          <div className="card" style={{ borderLeft: '4px solid #ef4444' }}>
            <div className="card-header"><h2>低出席率アラート（60%未満）</h2></div>
            <LowTable rows={low_attendance_alerts}/>
          </div>
        )}

        <div className="card">
          <div className="card-header"><h2>最近の出席アクティビティ</h2></div>
          {recent_activity.length === 0
            ? <EmptyState text="アクティビティがありません"/>
            : <ActivityTable rows={recent_activity}/>}
        </div>
      </div>
    );
  }

  // ── 標準（重要度順） ───────────────────────────────────────
  const alerts    = buildTeacherAlerts({ classes, low_attendance_alerts, openSessions });
  const avg       = Number(overview.average_attendance_rate) || 0;
  const liveCount = openSessions.length + otpCount;
  const stamped   = openSessions.reduce((n, s2) => n + s2.present + s2.late, 0);
  // 月ごとの推移。API が返すのは件数なので「率」ではなく「記録数」として見せる。
  // 月別の出席率を出すには、月ごとの実施回数×履修者数が要る（要API追加）。
  const trend     = monthly_trend.map(m => Number(m.total_attendance) || 0);

  return (
    <div className="page-container dash-priority">
      {header}

      {/* よく使う4つ */}
      {quickActions}

      {/* 出席受付は /otp にある。ここは状況だけ見せて送り出す */}
      <LinkStrip
        to="/otp"
        icon="hash"
        live={liveCount > 0}
        title={liveCount > 0 ? `出席を受付中（${liveCount}件）` : '出席を受け付ける'}
        meta={liveCount > 0
          ? openSessions.map(s2 => s2.className).join(' ・ ')
            + (stamped > 0 ? `　打刻 ${stamped}人` : '')
          : 'ビーコンまたはOTPコードで開始します'}
        action={liveCount > 0 ? '受付の画面へ' : '出席受付はこちら'}
      />

      {/* 要確認 */}
      <AlertCard items={alerts} emptyText="いま対応が必要なものはありません。"/>

      {/* 2層 */}
      <StatTiles>
        <StatTile hero label="平均出席率（全授業）" value={Math.round(avg)} unit="%"
                  color={avg < 60 ? '#dc2626' : avg < 80 ? '#d97706' : '#334155'}
                  note="60%未満の学生は上の「要対応」に出ています"/>
        <StatTile label="担当授業"   value={overview.total_classes || 0} unit="科目"/>
        <StatTile label="セッション" value={overview.total_sessions || 0} unit="回"/>
        <StatTile label="出席記録"   value={Number(overview.total_attendance_records || 0).toLocaleString('ja-JP')} unit="件"
                  note={trend.length > 1 ? '月別の記録数' : undefined}>
          <Sparkline values={trend} label={'月別の出席記録数 ' + trend.join('、')}/>
        </StatTile>
      </StatTiles>


      {/* 3層 */}
      {today_classes.length > 0 && (
        <Fold title="本日の授業" count={today_classes.length + '件'} open>
          <TodayTable rows={today_classes}/>
        </Fold>
      )}

      <Fold title="担当授業" count={classes.length + '科目'}>
        {classes.length === 0
          ? <EmptyState text="担当授業がありません"/>
          : <ClassesTable rows={classes}/>}
      </Fold>

      <Fold title="最近の出席アクティビティ" count={'直近' + Math.min(recent_activity.length, 15) + '件'}>
        {recent_activity.length === 0
          ? <EmptyState text="アクティビティがありません"/>
          : <ActivityTable rows={recent_activity}/>}
      </Fold>
    </div>
  );
}

// =========================================================================
//  要対応の組み立て
//    いまのAPIで分かるものだけを出す。「3回連続欠席」は dashboard API が
//    返していないので入れていない（学生一覧の streak と同じ計算が要る）。
// =========================================================================
function buildTeacherAlerts({ classes, low_attendance_alerts, openSessions }) {
  const items = [];

  // 60%未満の学生
  if (low_attendance_alerts.length > 0) {
    const byClass = {};
    low_attendance_alerts.forEach(a => { byClass[a.class_name] = (byClass[a.class_name] || 0) + 1; });
    items.push({
      key: 'low',
      level: 'crit',
      title: `出席率60%未満の学生 ${low_attendance_alerts.length}名`,
      meta: Object.entries(byClass).map(([n, c]) => `${n} ${c}名`).join(' ・ '),
      to: '/students',
      action: '学生管理',
      detailLabel: `${low_attendance_alerts.length}名の内訳を見る`,
      detail: <LowTable rows={low_attendance_alerts}/>,
    });
  }

  // 閉じ忘れたセッション（開始日が今日より前のまま受付中）
  const today = localDate();
  const stale = (openSessions || []).filter(
    s => s.sessionDate && String(s.sessionDate).slice(0, 10) < today
  );
  if (stale.length > 0) {
    items.push({
      key: 'stale',
      level: 'warn',
      title: `閉じ忘れた出席セッション ${stale.length}件`,
      // 受付中のものが残っていると、出席受付の画面で開始フォーム（授業の選択）が
      // 隠れる。「授業が選べない」ときの原因はたいていこれ。
      meta: stale.map(s => `${s.className}（${String(s.sessionDate).slice(0, 10)} 開始）`).join(' ・ ')
          + ' ─ 残っていると新しく開始できません',
      to: '/otp',
      action: '終了する',
    });
  }

  // 名簿が無い＝出席率の分母が作れない
  const noRoster = classes.filter(c => Number(c.total_students) === 0 && Number(c.total_sessions) > 0);
  if (noRoster.length > 0) {
    items.push({
      key: 'roster',
      level: 'warn',
      title: `履修者名簿が未登録の授業 ${noRoster.length}件`,
      meta: noRoster.map(c => c.name).join(' ・ ') + ' ─ 出席率が計算できません',
      to: '/classes',
      action: '名簿を取り込む',
    });
  }

  // 1回も実施していない
  const notHeld = classes.filter(c => Number(c.total_sessions) === 0);
  if (notHeld.length > 0) {
    items.push({
      key: 'nothold',
      level: 'note',
      title: `まだ1回も実施していない授業 ${notHeld.length}件`,
      meta: notHeld.map(c => c.name).join(' ・ ') + ' ─ 出席率は「—」のままです',
      to: '/classes',
      action: '授業管理',
    });
  }

  return items;
}

function localDate() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// =========================================================================
//  表（2つの並べ方で使い回す）
// =========================================================================
function TodayTable({ rows }) {
  return (
    <TableWrap>
      <table className="data-table">
        <thead><tr><th>授業名</th><th>時間</th><th>教室</th><th>出席</th></tr></thead>
        <tbody>
          {rows.map((c, i) => (
            <tr key={i}>
              <td><strong>{c.name}</strong></td>
              <td>{c.start_time} 〜 {c.end_time}</td>
              <td>{c.location || '-'}</td>
              <td>
                <strong>{c.current_attendance}</strong> / {c.total_students}
                <span style={{ color: '#888', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                  ({c.attendance_percentage}%)
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function ClassesTable({ rows }) {
  return (
    <TableWrap>
      <table className="data-table">
        <thead><tr><th>授業名</th><th>学期</th><th>履修者数</th><th>セッション</th><th>出席率</th></tr></thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.id}>
              <td><strong>{c.name}</strong></td>
              <td>{c.semester || '-'}</td>
              <td>
                {c.total_students}
                {Number(c.total_students) === 0 && (
                  <span style={{ color: '#b45309', fontSize: '0.8rem', marginLeft: '0.35rem' }}>
                    名簿なし
                  </span>
                )}
              </td>
              <td>{c.total_sessions}</td>
              {/* 分母が作れないときは 0% ではなく「—」。
                  0% は「実施したのに全員欠席」だけを意味させる【2026-09-11】 */}
              <td>
                {c.attendance_rate == null
                  ? <span style={{ color: '#6b7280' }}>—</span>
                  : <strong>{c.attendance_rate}%</strong>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function LowTable({ rows }) {
  return (
    <TableWrap>
      <table className="data-table">
        <thead><tr><th>学籍番号</th><th>氏名</th><th>授業</th><th>出席率</th><th>出席/総数</th></tr></thead>
        <tbody>
          {rows.map((a, i) => (
            <tr key={i}>
              <td>{a.student_id}</td>
              <td>{a.student_name}</td>
              <td>{a.class_name}</td>
              <td><strong style={{ color: '#ef4444' }}>{a.attendance_rate}%</strong></td>
              <td>{a.attended_sessions} / {a.total_sessions}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function ActivityTable({ rows }) {
  return (
    <TableWrap>
      <table className="data-table">
        <thead><tr><th>時刻</th><th>学生</th><th>授業</th><th>教室</th><th>状態</th></tr></thead>
        <tbody>
          {rows.slice(0, 15).map((r, i) => (
            <tr key={i}>
              <td>{formatTime(r.timestamp)}</td>
              <td>{r.student_name} ({r.student_id})</td>
              <td>{r.class_name}</td>
              <td>{r.location || '-'}</td>
              <td><StatusBadge status={r.status}/></td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

// ── 元の表示でしか使わない部品 ─────────────────────────────────
function QuickCard({ to, icon, title, desc, color }) {
  return (
    <Link to={to} className="quick-card" style={{ borderTop: '4px solid ' + color }}>
      <div className="quick-icon" style={{ background: color + '15', color }}>{icon}</div>
      <div className="quick-title">{title}</div>
      <div className="quick-desc">{desc}</div>
    </Link>
  );
}

function StatCard({ label, value, unit, color }) {
  return (
    <div className="stat-card" style={{ borderTop: '4px solid ' + color }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-unit">{unit}</div>
    </div>
  );
}
